import type { BurnoutBand } from "../../dashboardApi";
import { READINESS_BANDS, TSB_DEEP_FATIGUE } from "../../lib/scienceThresholds";

/**
 * todayHeroUtils — pure logic for the Today hero card (W-REBUILD-1).
 *
 * Extracted per the extract-pure→test convention: readout selection and
 * raw-reason humanization are plain functions with explicit args so the
 * unit tests don't need router/query mocks.
 */

export type HeroTone = "red" | "amber" | "green" | "bright" | "muted";

export interface HeroReadout {
  /** i18n key inside the "dashboard" namespace (hero.*). */
  key: string;
  tone: HeroTone;
  /** Burnout trend is the source → tap opens the detail expansion. */
  fromBurnout: boolean;
}

/**
 * Фолбэк НА КАЖДОЕ состояние. Раньше на все ветки стоял один defaultValue —
 * зелёный «Готов к плану», поэтому пропавший ключ (переименование, чужой
 * namespace) заставлял карточку обещать зелёный свет поверх красного кольца.
 * Это поверхность безопасности: молчаливая деградация обязана сохранять смысл.
 */
export const HERO_READOUT_FALLBACK_RU: Record<string, string> = {
  "hero.readout_burnout_red": "Перегруз рядом — сегодня легче, чем хочется, и больше сна.",
  "hero.readout_burnout_amber": "Усталость копится не первую неделю — оставь запас сегодня.",
  "hero.readout_burnout_yellow": "Один из трендов просел — понаблюдай пару дней.",
  "hero.readout_no_data": "Данных мало — отметь самочувствие, и появится готовность.",
  "hero.readout_bright": "Отличная готовность — день для качественной работы.",
  "hero.readout_bright_deep_fatigue":
    "Организм готов, но свежесть глубоко в минусе — качество можно короткое, объём придержи.",
  "hero.readout_green": "Готов к плану — зелёный свет.",
  "hero.readout_amber": "Готовность средняя — держи интенсивность под контролем.",
  "hero.readout_red": "Тело просит отдыха — восстановление сегодня важнее плана.",
};

/**
 * Pick the single human readout line for the hero.
 *
 * Priority: multi-week burnout trend (worst news first, it's the safety
 * surface) → today's readiness band → no-data prompt.
 *
 * tsb (2026-08-12): readiness считается от самочувствия (HRV/сон/пульс) и
 * при глубоко отрицательной свежести честно показывал «отличная готовность —
 * день для качественной работы», пока «Форма» и Studio в тот же день писали
 * «глубокая усталость — сначала разгрузка». Один атлет, один день, два
 * противоположных совета. Обещание качества умеряется, когда TSB ниже
 * канонического порога (та же константа, что красит бейдж «глубокая
 * усталость» — не второй порог).
 */
export function pickHeroReadout(
  burnoutBand: BurnoutBand | null,
  readinessScore: number | null,
  tsb: number | null = null,
): HeroReadout {
  if (burnoutBand === "red") {
    return { key: "hero.readout_burnout_red", tone: "red", fromBurnout: true };
  }
  if (burnoutBand === "amber") {
    return { key: "hero.readout_burnout_amber", tone: "amber", fromBurnout: true };
  }
  if (burnoutBand === "yellow") {
    return { key: "hero.readout_burnout_yellow", tone: "amber", fromBurnout: true };
  }
  if (readinessScore == null) {
    return { key: "hero.readout_no_data", tone: "muted", fromBurnout: false };
  }
  const deepFatigue = tsb != null && tsb < TSB_DEEP_FATIGUE;
  if (readinessScore >= READINESS_BANDS.bright) {
    if (deepFatigue) {
      return { key: "hero.readout_bright_deep_fatigue", tone: "amber", fromBurnout: false };
    }
    return { key: "hero.readout_bright", tone: "bright", fromBurnout: false };
  }
  if (readinessScore >= READINESS_BANDS.green) {
    if (deepFatigue) {
      return { key: "hero.readout_bright_deep_fatigue", tone: "amber", fromBurnout: false };
    }
    return { key: "hero.readout_green", tone: "green", fromBurnout: false };
  }
  if (readinessScore >= READINESS_BANDS.amber) {
    return { key: "hero.readout_amber", tone: "amber", fromBurnout: false };
  }
  return { key: "hero.readout_red", tone: "red", fromBurnout: false };
}

export interface HumanReason {
  /** Metric label — i18n key when recognised, raw prefix otherwise. */
  labelKey: string | null;
  rawLabel: string;
  score: number | null;
  /**
   * Human tail: kept from the source when it's already human, null when
   * the source tail is technical (z-scores, σ, lnRMSSD, x/100) — caller
   * substitutes a band phrase instead.
   */
  tail: string | null;
  severity: "ok" | "watch" | "alert";
  /**
   * Направление метрики: у большинства «больше = лучше», у пульса покоя —
   * наоборот. Нужно, чтобы фраза-диапазон описывала ВЕЛИЧИНУ, а не балл.
   */
  higherIsBetter: boolean;
}

const METRIC_LABEL_KEYS: Record<string, string> = {
  load: "reason.metric_load",
  hrv: "reason.metric_hrv",
  rhr: "reason.metric_rhr",
};

/**
 * Метрики, где БОЛЬШЕ = ХУЖЕ, и потому суб-скор инвертирован относительно
 * самой величины: `readiness_v2._rhr_component` считает балл через
 * `_score_from_zscore(z, higher_is_better=False)`.
 *
 * Инцидент 2026-08-10. Фразы диапазона писались для метрик «больше = лучше»
 * (HRV, регулярность сна) и переиспользовались дословно. На проде атлет видел
 * «Пульс покоя · ниже твоей нормы» рядом с меткой «СЛЕДИМ» — тогда как низкий
 * балл означает ПОВЫШЕННЫЙ пульс. Утверждение читалось ровно наоборот, на
 * сигнале здоровья, и подпись «следим» выглядела необъяснимой.
 */
const HIGHER_IS_WORSE = new Set(["rhr"]);

/** Formula/debug tails never reach the athlete (canon: формулы — в код). */
const TECH_TAIL = /z\s*=|σ|lnrmssd|rmssd|\/100|\bz-?score\b/i;

/**
 * Parse a backend color_reason like
 *   "🔴 load: 45/100 — нагрузка 203 TSS за 3 дня"
 *   "🟡 HRV: 50/100 — lnRMSSD z=-0.99σ"
 * into a human-presentable structure. Emoji prefix is dropped (severity
 * dot already carries the colour), technical tails are suppressed.
 */
export function humanizeReason(reason: string): HumanReason {
  // Strip a leading emoji/pictograph and whitespace.
  const cleaned = reason.replace(/^[^\p{L}\p{N}]+/u, "").trim();
  const colonIdx = cleaned.indexOf(":");
  const rawLabel = (colonIdx > 0 ? cleaned.slice(0, colonIdx) : cleaned.split("—")[0] ?? cleaned)
    .trim()
    .slice(0, 32);
  const scoreMatch = cleaned.match(/(\d{1,3})\s*\/\s*100/);
  const score = scoreMatch ? Math.min(100, parseInt(scoreMatch[1], 10)) : null;

  const dashIdx = cleaned.indexOf("—");
  let tail: string | null = dashIdx >= 0 ? cleaned.slice(dashIdx + 1).trim() : null;
  if (tail && TECH_TAIL.test(tail)) tail = null;
  if (tail === "") tail = null;
  // «Нагрузка · нагрузка 203 TSS…» — хвост не должен повторять метку.
  if (tail && tail.toLowerCase().startsWith(rawLabel.toLowerCase())) {
    tail = tail.slice(rawLabel.length).replace(/^[\s:—·-]+/, "").trim() || null;
  }

  const labelKey = METRIC_LABEL_KEYS[rawLabel.toLowerCase()] ?? null;

  const severity: HumanReason["severity"] =
    score == null ? "watch" : score < 50 ? "alert" : score < 70 ? "watch" : "ok";

  const higherIsBetter = !HIGHER_IS_WORSE.has(rawLabel.toLowerCase());

  return { labelKey, rawLabel, score, tail, severity, higherIsBetter };
}

/**
 * Band phrase i18n key for a 0-100 sub-score (used when tail is technical).
 *
 * Фраза описывает ВЕЛИЧИНУ метрики, а балл — лишь способ о ней узнать. Для
 * метрик «больше = хуже» (пульс покоя) низкий балл значит высокую величину,
 * поэтому фразу зеркалим. Верхние два диапазона не зеркалятся: «в пределах
 * нормы» и «лучше обычного» не называют направление и верны для обеих сторон.
 */
export function reasonBandKey(score: number | null, higherIsBetter = true): string {
  if (score == null) return "reason.band_unknown";
  if (score < 50) return higherIsBetter ? "reason.band_low" : "reason.band_high";
  if (score < 70) return higherIsBetter ? "reason.band_below" : "reason.band_above";
  if (score < 85) return "reason.band_normal";
  return "reason.band_great";
}

/** Format seconds as h:mm:ss / m:ss for race target chips. */
export function formatRaceTime(s: number | null | undefined): string | null {
  if (s == null || s <= 0) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s - h * 3600) / 60);
  const sec = Math.round(s - h * 3600 - m * 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}
